import { tokenizeMentions } from '../lib/mentions.js';

/**
 * Canonical renderer for a body that may contain @mentions. Draws structured
 * `@[Name](user:uuid)` tokens as `.mention-chip`s (React-escaped text, never
 * innerHTML). For backward compatibility with legacy comments, bare `@handle`
 * runs inside plain text segments are also chipped (the old IssueDetail
 * behavior), so historical `@handle` comments still highlight.
 *
 * Shared by messenger and PM issue comments so both surfaces render mentions
 * identically.
 */

// Split on standalone @handles. The negative lookbehind keeps us from chipping
// the domain of an email like `jane@example.com` (the `@` there follows a word
// char). Anchored, non-global IS_LEGACY_HANDLE tests split parts (no lastIndex).
const LEGACY_SPLIT = /(?<![\w.])(@[a-zA-Z][\w.-]*)/g;
const IS_LEGACY_HANDLE = /^@[a-zA-Z][\w.-]*$/;

// Split a plain-text run on legacy @handles, chipping the handles.
function renderLegacy(text, keyPrefix) {
  if (!text) return text;
  const parts = text.split(LEGACY_SPLIT);
  return parts.map((p, i) =>
    IS_LEGACY_HANDLE.test(p)
      ? <span key={`${keyPrefix}-h${i}`} className="mention-chip">{p}</span>
      : <span key={`${keyPrefix}-t${i}`}>{p}</span>
  );
}

export default function MentionText({ body }) {
  const segments = tokenizeMentions(body);
  return (
    <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      {segments.map((s, i) =>
        s.type === 'mention' ? (
          <span key={`m${i}`} className="mention-chip" data-testid="mention-chip" data-user-id={s.userId}>
            @{s.display}
          </span>
        ) : (
          <span key={`s${i}`}>{renderLegacy(s.value, `s${i}`)}</span>
        )
      )}
    </span>
  );
}
